import { ImageDropZone } from '@/components/profile/image-drop-zone';
import type { ProfileImage } from '@/components/profile/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { uploadPhaseLabel, useImageUpload } from '@/hooks/use-image-upload';
import { formatBytes, MAX_IMAGE_DIMENSION, MAX_UPLOAD_BYTES } from '@/lib/image-upload';

export interface AvatarCardProps {
  /** What to render. Null is the placeholder, not an error. */
  avatarUrl: string | null;
  /** The `user_images` row behind it, when there is one; removing needs its id. */
  avatarImageId: string | null;
  /**
   * Where that row is in moderation, or null when there is no uploaded row behind the picture
   * (never uploaded one, or signed in with Google and kept theirs — that one is not moderated
   * here because it was never uploaded here).
   */
  avatarStatus: ProfileImage['status'] | null;
  isRemoving: boolean;
  removeError: string | null;
  onRemove: () => void;
}

export function AvatarCard({
  avatarUrl,
  avatarImageId,
  avatarStatus,
  isRemoving,
  removeError,
  onRemove,
}: AvatarCardProps) {
  const uploader = useImageUpload('avatar');
  const status = uploadPhaseLabel(uploader.phase);
  const hasAvatar = avatarUrl !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile picture</CardTitle>
        <CardDescription>
          Shown next to your name. Uploading a new one replaces the old, and a moderator checks it
          before anybody else sees it.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-6">
        <ImageDropZone
          id="avatar"
          label={hasAvatar ? 'Replace your profile picture' : 'Upload a profile picture'}
          disabled={uploader.isUploading}
          onFile={uploader.upload}
          className="size-28 shrink-0 overflow-hidden rounded-full p-2"
        >
          {avatarUrl ? (
            // Decorative: the button around it already says what this is.
            <img src={avatarUrl} alt="" className="size-full object-cover" />
          ) : (
            <span>Drop a photo, or click to choose</span>
          )}
        </ImageDropZone>

        <div className="min-w-48 flex-1 space-y-2">
          <p className="text-sm text-muted-foreground">
            PNG, JPEG or WebP, up to {formatBytes(MAX_UPLOAD_BYTES)}. Anything larger than{' '}
            {MAX_IMAGE_DIMENSION} px is resized in your browser before it is uploaded.
          </p>

          {/* The owner is the one person shown an unapproved picture, so this is where they
              find out why nobody else can see it. A rejected one has had its bytes deleted from
              the bucket, which is why there is nothing above to look at. */}
          {avatarStatus === 'pending' && (
            <p className="text-sm font-medium" role="status">
              Waiting for a moderator. Nobody else sees it until it is approved.
            </p>
          )}
          {avatarStatus === 'rejected' && (
            <p className="text-sm font-medium" role="status">
              A moderator did not approve this picture. Upload a different one.
            </p>
          )}

          {status && (
            <p className="text-sm font-medium" role="status">
              {status}
            </p>
          )}
          {uploader.error && (
            <p className="text-sm text-destructive" role="alert">
              {uploader.error}
            </p>
          )}
          {removeError && (
            <p className="text-sm text-destructive" role="alert">
              {removeError}
            </p>
          )}

          {avatarImageId !== null && (
            <Button
              variant="outline"
              size="sm"
              disabled={isRemoving || uploader.isUploading}
              onClick={onRemove}
            >
              {isRemoving ? 'Removing…' : 'Remove picture'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
